import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  X, Calendar, Clock, MapPin, DollarSign, User, Phone, Mail,
  Briefcase, AlertTriangle, FileText, CreditCard,
  Home, Building, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { bookingsAPI, servicesAPI, workersAPI } from '../../services/api';

const CreateBookingModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Service, 2: Customer, 3: Details, 4: Review
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [workers, setWorkers] = useState([]);
  
  const [formData, setFormData] = useState({
    // Service selection
    service_id: '',
    service_type: 'errands',
    service_name: '',
    
    // Schedule
    scheduled_date: '',
    scheduled_time: '',
    duration_hours: 2,
    is_urgent: false,
    
    // Pricing
    hourly_rate: 25,
    total_amount: 50,
    payment_type: 'full', // full, deposit
    
    // Customer info
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    
    // Location
    address: '',
    city: '',
    state: 'NC',
    zip_code: '',
    
    // Assignment
    worker_id: '',
    
    // Notes
    notes: '',
    
    // Additional client/property info (for service details)
    client_info: {
      property_size: '',
      special_requirements: '',
      access_instructions: '',
      emergency_contact: '',
      emergency_phone: ''
    }
  });

  useEffect(() => {
    fetchServices();
    fetchWorkers();
  }, []);

  useEffect(() => {
    // Calculate total
    const total = formData.hourly_rate * formData.duration_hours;
    const urgentMultiplier = formData.is_urgent ? 1.25 : 1;
    setFormData(prev => ({ ...prev, total_amount: total * urgentMultiplier }));
  }, [formData.hourly_rate, formData.duration_hours, formData.is_urgent]);

  const fetchServices = async () => {
    try {
      const res = await servicesAPI.getAll();
      setServices(res.data || []);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  };

  const fetchWorkers = async () => {
    try {
      const res = await workersAPI.getAll();
      setWorkers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch workers:', err);
    }
  };

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setFormData(prev => ({
        ...prev,
        service_id: serviceId,
        service_name: service.name,
        service_type: service.service_type,
        hourly_rate: service.hourly_rate || service.base_rate || 25
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build booking data
      const bookingData = {
        service_id: formData.service_id,
        service_name: formData.service_name,
        service_type: formData.service_type,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        duration_hours: formData.duration_hours,
        hourly_rate: formData.hourly_rate,
        total_amount: formData.total_amount,
        is_urgent: formData.is_urgent,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        notes: formData.notes,
        worker_id: formData.worker_id || null,
        payment_type: formData.payment_type,
        client_info: formData.client_info,
        status: formData.worker_id ? 'confirmed' : 'pending',
        created_by_owner: true
      };
      
      await bookingsAPI.createManual(bookingData);
      toast.success('Booking created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="modal-content max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold">Create New Booking</h2>
            <p className="text-[var(--text-muted)] text-sm">Manually create a booking with all details</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--background-subtle)] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 px-4">
          {['Service', 'Customer', 'Details', 'Review'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step > idx + 1 ? 'bg-green-500 text-white' :
                step === idx + 1 ? 'bg-[var(--primary)] text-white' : 'bg-[var(--background-subtle)] text-[var(--text-muted)]'
              }`}>
                {step > idx + 1 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
              </div>
              <span className={`ml-2 text-sm ${step === idx + 1 ? 'font-medium' : 'text-[var(--text-muted)]'}`}>{label}</span>
              {idx < 3 && <div className={`w-12 h-0.5 mx-3 ${step > idx + 1 ? 'bg-green-500' : 'bg-[var(--background-subtle)]'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label className="text-lg font-semibold mb-4 block">Select Service</Label>
              <div className="grid md:grid-cols-2 gap-4">
                {services.map(service => (
                  <div
                    key={service.id}
                    onClick={() => handleServiceSelect(service.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.service_id === service.id 
                        ? 'border-[var(--primary)] bg-[var(--accent)]' 
                        : 'border-[var(--border-primary)] hover:border-[var(--border-primary)]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-8 h-8 text-[var(--success)]" />
                      <div>
                        <h4 className="font-semibold">{service.name}</h4>
                        <p className="text-sm text-[var(--text-muted)]">${service.hourly_rate || service.base_rate || 25}/hr</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Schedule */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>Duration (hours) *</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: parseInt(e.target.value) })}
                  className="input-styled mt-1"
                  required
                />
              </div>
            </div>

            {/* Urgent */}
            <div className="p-4 bg-[var(--warning)]/10 rounded-xl border border-[var(--warning)]/30">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.is_urgent}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_urgent: checked })}
                />
                <div>
                  <span className="font-semibold text-amber-700">Mark as Urgent (+25%)</span>
                  <p className="text-sm text-amber-600">Urgent bookings are prioritized and have a 25% surcharge</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Step 2: Customer Info */}
        {step === 2 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-[var(--primary)]" />
              Customer Information
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Phone *</Label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                className="input-styled mt-1"
                required
              />
            </div>

            <h3 className="text-lg font-semibold flex items-center gap-2 pt-4">
              <MapPin className="w-5 h-5 text-[var(--primary)]" />
              Service Location
            </h3>

            <div>
              <Label>Street Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-styled mt-1"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>State *</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>ZIP Code *</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Service Details */}
        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-[var(--success)]" />
              Service Details
            </h3>

            <div>
              <Label>Special Requirements</Label>
              <Textarea
                value={formData.client_info.special_requirements}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  client_info: { ...formData.client_info, special_requirements: e.target.value }
                })}
                className="input-styled mt-1"
                placeholder="Any special requirements for this service..."
                rows={3}
              />
            </div>

            <div>
              <Label>Access Instructions</Label>
              <Textarea
                value={formData.client_info.access_instructions}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  client_info: { ...formData.client_info, access_instructions: e.target.value }
                })}
                className="input-styled mt-1"
                placeholder="Gate codes, parking info, entry instructions..."
                rows={2}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Emergency Contact Name</Label>
                <Input
                  value={formData.client_info.emergency_contact}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    client_info: { ...formData.client_info, emergency_contact: e.target.value }
                  })}
                  className="input-styled mt-1"
                  placeholder="Contact name"
                />
              </div>
              <div>
                <Label>Emergency Contact Phone</Label>
                <Input
                  value={formData.client_info.emergency_phone}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    client_info: { ...formData.client_info, emergency_phone: e.target.value }
                  })}
                  className="input-styled mt-1"
                  placeholder="(xxx) xxx-xxxx"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-styled mt-1"
                placeholder="Any additional notes for this booking..."
              />
            </div>
          </div>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Review Booking</h3>

            {/* Summary Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--accent)] rounded-xl">
                <h4 className="font-semibold mb-2">Service</h4>
                <p className="font-bold text-lg">{formData.service_name}</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {formData.scheduled_date} at {formData.scheduled_time}
                </p>
                <p className="text-sm">{formData.duration_hours} hours</p>
                {formData.is_urgent && (
                  <span className="inline-flex items-center gap-1 mt-2 text-amber-600 text-sm">
                    <AlertTriangle className="w-4 h-4" /> URGENT +25%
                  </span>
                )}
              </div>

              <div className="p-4 bg-[var(--success)]/10 rounded-xl">
                <h4 className="font-semibold mb-2">Pricing</h4>
                <p className="text-sm">Rate: ${formData.hourly_rate}/hr x {formData.duration_hours}hrs</p>
                {formData.is_urgent && <p className="text-sm text-amber-600">Urgent surcharge: +25%</p>}
                <p className="font-bold text-2xl text-[var(--success)] mt-2">${formData.total_amount.toFixed(2)}</p>
              </div>
            </div>

            <div className="p-4 border rounded-xl">
              <h4 className="font-semibold mb-2">Customer</h4>
              <p>{formData.customer_name}</p>
              <p className="text-sm text-[var(--text-muted)]">{formData.customer_email} | {formData.customer_phone}</p>
              <p className="text-sm mt-2">
                {formData.address}, {formData.city}, {formData.state} {formData.zip_code}
              </p>
            </div>

            {/* Worker Assignment */}
            <div className="p-4 border rounded-xl">
              <h4 className="font-semibold mb-2">Assign Worker (Optional)</h4>
              <Select 
                value={formData.worker_id || 'unassigned'}
                onValueChange={(v) => setFormData({ ...formData, worker_id: v === 'unassigned' ? '' : v })}
              >
                <SelectTrigger className="input-styled" data-testid="assign-worker-select">
                  <SelectValue placeholder="Select a worker or leave unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Leave Unassigned (Post to Job Board)</SelectItem>
                  {workers
                    .filter(w => w.service_types?.includes(formData.service_type))
                    .map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            {/* Payment Type */}
            <div className="p-4 border rounded-xl">
              <h4 className="font-semibold mb-2">Payment Status</h4>
              <div className="flex gap-4">
                <label className={`flex-1 cursor-pointer p-3 rounded-lg border-2 text-center ${
                  formData.payment_type === 'full' ? 'border-[var(--success)] bg-[var(--success)]/10' : 'border-[var(--border-primary)]'
                }`}>
                  <input
                    type="radio"
                    name="payment_type"
                    value="full"
                    checked={formData.payment_type === 'full'}
                    onChange={() => setFormData({ ...formData, payment_type: 'full' })}
                    className="sr-only"
                  />
                  <span className="font-medium">Full Payment</span>
                  <p className="text-sm text-[var(--text-muted)]">${formData.total_amount.toFixed(2)}</p>
                </label>
                <label className={`flex-1 cursor-pointer p-3 rounded-lg border-2 text-center ${
                  formData.payment_type === 'deposit' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[var(--border-primary)]'
                }`}>
                  <input
                    type="radio"
                    name="payment_type"
                    value="deposit"
                    checked={formData.payment_type === 'deposit'}
                    onChange={() => setFormData({ ...formData, payment_type: 'deposit' })}
                    className="sr-only"
                  />
                  <span className="font-medium">20% Deposit</span>
                  <p className="text-sm text-[var(--text-muted)]">${(formData.total_amount * 0.2).toFixed(2)}</p>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Footer Navigation */}
        <div className="flex gap-4 mt-8 pt-6 border-t">
          {step > 1 && (
            <Button type="button" onClick={() => setStep(step - 1)} className="btn-secondary">
              Back
            </Button>
          )}
          <div className="flex-1" />
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)} className="btn-primary">
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="btn-primary">
              {loading ? 'Creating...' : 'Create Booking'}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CreateBookingModal;
